import type { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { AgencySidebar } from "@/components/agency-sidebar"
import { AppContent } from "@/components/app-content"
import { AppShell } from "@/components/app-shell"
import { BrandSwitcher } from "@/components/brand-switcher"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { HeaderNavUser } from "@/components/header-nav-user"
import { BellTrigger } from "@/components/notifications/bell-trigger"
import { RoleBadge } from "@/components/role-badge"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { usePollUnreadCount } from "@/hooks/use-poll-unread-count"
import {
  FIXED_VIEWPORT_CHAIN,
  useIsFixedViewportSurface,
} from "@/layouts/app/fixed-viewport-surface"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface AgencyShellProps {
  breadcrumbs?: BreadcrumbItem[]
}

/**
 * AgencyShell — shell for `agency` role (OKS-13).
 *
 * F1 SPLIT: dedicated shell mounting `<AgencySidebar>` rather than the
 * shared `BrandShell` (which mounts `BrandSidebar`). Brand and agency
 * consoles diverge structurally starting OKS-13 — agency gets Brands +
 * Team pages that brand never has, and the divergence will only widen.
 *
 * Otherwise mirrors `AdminShell` chrome: header with sidebar trigger +
 * breadcrumbs + brand switcher + role badge + nav user. Content region
 * is a `tabIndex={-1}` `<div role="region">` to avoid nested `<main>`
 * (same rationale as the other shells — the page itself owns the
 * `<main>` semantics).
 *
 * **OKS-21 CTPO addition** — `<BrandSwitcher />` joins the header slot
 * between the spacer and the role badge, mirroring `BrandShell`'s
 * pattern. The component's built-in visibility guard
 * (`role === "agency" && availableBrands.length > 1`) means
 * single-brand agencies see nothing rendered. Closes a pre-existing
 * OKS-13 F1-split gap where agency users with multiple brand
 * assignments couldn't switch tenancy from inside the agency console.
 *
 * The `<RoleBadge role="agency">` makes scope unambiguous to staff who
 * toggle between brand/agency tabs.
 */
export function AgencyShell({
  children,
  breadcrumbs = [],
}: PropsWithChildren<AgencyShellProps>) {
  const { t } = useTranslation()
  // OKS-36 — bell badge polling. Single 30s tick at the shell level.
  usePollUnreadCount()

  // OKS-1041 — agency users reach `/crew/messages` as a first-class audience
  // (`ConversationPolicy#brand_side_member?` grants any actively-assigned
  // agency member, and `AgencySidebar` links straight to it), but this shell
  // never got OKS-820's fixed-viewport chain. Inside `app-layout`'s
  // `h-[100dvh] overflow-hidden` wrapper the sidebar wrapper's `min-h-svh`
  // kept the stack taller than the fold, and `overflow-hidden` CLIPPED the
  // surplus — so the composer was not merely below the fold, it was
  // unreachable (the wrapper that hid it also forbids page scroll).
  //
  // Same chain, same source as `BrandShell`, so the two cannot drift again.
  const isFixedViewportSurface = useIsFixedViewportSurface()

  return (
    <AppShell
      variant="sidebar"
      className={cn(isFixedViewportSurface && FIXED_VIEWPORT_CHAIN.shell)}
    >
      <AgencySidebar />
      <AppContent
        variant="sidebar"
        className={cn(
          "overflow-x-hidden",
          isFixedViewportSurface && FIXED_VIEWPORT_CHAIN.content,
        )}
      >
        <header
          className="border-sidebar-border/50 flex h-16 shrink-0 items-center gap-3 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:px-6"
          data-testid="agency-shell-header"
        >
          <SidebarTrigger className="-ml-1" />
          <div className="hidden lg:flex">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <div className="flex-1" />
          <BrandSwitcher />
          <RoleBadge role="agency" />
          <BellTrigger />
          <HeaderNavUser />
        </header>
        <div
          tabIndex={-1}
          role="region"
          aria-label={t("shell.content_region_label")}
          // OKS-1041 — bounded flex column on the Messages route so the page's
          // own `flex min-h-0 flex-1` band sizes to the remaining height and
          // scrolls internally. Every other agency page keeps the plain
          // growing `flex-1` = body-scroll, unchanged.
          className={cn(
            isFixedViewportSurface ? FIXED_VIEWPORT_CHAIN.region : "flex-1",
          )}
        >
          {children}
        </div>
      </AppContent>
    </AppShell>
  )
}
